import { StoreBadgeDialogComponent } from '@/components/store-badge-dialog/store-badge-dialog.component';
import { AppConstants } from '@/helpers/app.constants';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { AuthenticationService } from '@/services/authentication.service';
import { BridgeInputService } from '@/services/bridge/bridge-input.service';
import { BridgeService, Credentials } from '@/services/bridge/bridge.service';
import { EmailWithTLDValidator } from '@/validators/EmailWithTLDValidator';
import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink, Params } from '@angular/router';
import { IdentityProvider, IdentityProviderType } from '@eemo/api-client';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { NgTemplateOutlet } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CardBaseComponent } from '../../../components/base/card-base/card-base.component';

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CardBaseComponent,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    TooltipDirective,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);
  private alertService = inject(AlertService);
  appConfigService = inject(AppConfigService);
  private titleService = inject(Title);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private renderer = inject(Renderer2);
  private dialog = inject(MatDialog);
  private bridgeInputService = inject(BridgeInputService);

  private passwordField: ElementRef;
  @ViewChild('password') set content(content: ElementRef) {
    if (content) {
      // initially setter gets called with undefined
      this.passwordField = content;
    }
  }

  usernameSub = new Subscription();
  passwordSub = new Subscription();
  recaptchaSub = new Subscription();
  redirectLoginSub = new Subscription();
  b2cLoginSub = new Subscription();
  credentialSub = new Subscription();

  loginForm: UntypedFormGroup;
  loading = false;
  submited = false;
  returnUrl: string;

  login: string;
  noAccount = false;
  email: string;
  changeUser = false;

  b2cUser = false;
  currentIdp: IdentityProvider;
  hidePassword = true;
  passwordFilled = false;
  passwordPreviousValue = '';

  constructor() {
    // redirect to home if already logged in
    if (this.authenticationService.currentUserValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit() {
    this.initSubscriptions();
    if (!this.appConfigService.isMobileApp) {
      this.renderer.addClass(document.body, 'recaptcha');
      this.renderer.addClass(document.body, 'httpcs');
    }
    if (this.appConfigService.isDemo()) {
      this.router.navigate(['/login/guest']);
      return;
    }

    this.titleService.setTitle('EEMO - Connexion');

    // if mobile web or tablet, show dialog download app
    if (this.appConfigService.showAppDownload) {
      this.showDownloadApp();
    }

    // get return url from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams[AppConstants.PARAM_RETURN_URL] || '/';
    this.noAccount = this.route.snapshot.queryParams[AppConstants.PARAM_NO_ACCOUNT];
    this.email = this.route.snapshot.queryParams[AppConstants.PARAM_EMAIL];
    this.changeUser = this.route.snapshot.queryParams[AppConstants.PARAM_CHANGE_USER];

    this.login = this.authenticationService.getSavedUserLogin();
    this.loginForm = this.formBuilder.group({
      username: [this.email ? this.email : this.login, [Validators.required, EmailWithTLDValidator]],
      password: ['', Validators.required],
    });

    this.currentIdp = this.authenticationService.getSavedUserIdp();
    this.changeB2cUser(this.login && this.currentIdp && IdentityProviderType.B2C === this.currentIdp.type);

    if (this.noAccount) {
      // login from a sharing mail with no account associated
      // delete the previous user login and redirect to the register page with the email specified in the link
      this.authenticationService.deleteSavedUserLogin();
      const queryParams = {};
      if (this.email) {
        queryParams[AppConstants.PARAM_EMAIL] = this.email;
        queryParams[AppConstants.PARAM_RETURN_URL] = this.returnUrl;
      }
      this.router.navigate(['register'], { queryParams });
    }

    const userNameField = this.f('username');
    if (userNameField.value) {
      this.f('username').markAsTouched();
    }

    this.usernameSub = userNameField.valueChanges.subscribe(() => {
      if (userNameField.value !== this.login) {
        this.changeB2cUser(false);
        this.login = userNameField.value;
      }
    });
    this.passwordSub = this.f('password').valueChanges.subscribe(
      () => (this.passwordFilled = this.f('password').value ? true : false),
    );
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'recaptcha');
    this.renderer.removeClass(document.body, 'httpcs');
    this.usernameSub.unsubscribe();
    this.passwordSub.unsubscribe();
    this.recaptchaSub.unsubscribe();
    this.redirectLoginSub.unsubscribe();
    this.b2cLoginSub.unsubscribe();
    this.credentialSub.unsubscribe();
  }

  changeB2cUser(value: boolean) {
    this.b2cUser = value;
    this.f('password').setErrors(null);
    if (this.b2cUser) {
      setTimeout(() => {
        this.f('password').setValidators(Validators.required);
        if (this.passwordField) {
          this.passwordField.nativeElement.focus();
          BridgeService.askCredentials(true);
        }
      });
    } else {
      this.f('password').clearValidators();
    }
    this.f('password').updateValueAndValidity();
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.loginForm.get(field);
  }

  getQueryParams(): Params {
    const queryParams: Params = {};
    queryParams[AppConstants.PARAM_RETURN_URL] = this.returnUrl;
    queryParams[AppConstants.PARAM_EMAIL] = this.f('username').value;
    return queryParams;
  }

  onSubmit() {
    // reset alerts on submit
    this.alertService.clear();
    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.submited = true;
    this.loading = true;
    if (environment.recaptchaEnabled) {
      this.recaptchaSub = this.recaptchaV3Service.execute('getUserIdp').subscribe({
        next: (token) => {
          this.doLogin(token);
        },
        error: () => {
          this.alertService.badRequest('Error trying to verify the ReCaptcha token');
        },
      });
    } else {
      this.doLogin(null);
    }
  }

  doLogin(recaptchaToken: string) {
    const userLogin = this.f('username').value.toLowerCase();
    if (this.b2cUser) {
      const password = this.f('password').value;
      // do login direclty
      this.b2cLoginSub = this.authenticationService
        .doB2CLogin(this.currentIdp, userLogin, password, this.returnUrl)
        .subscribe({
          next: (result) => {
            if (result) {
              this.titleService.setTitle('EEMO');
              BridgeService.saveCredentials({ username: userLogin, password: password });
              // if succeed, we let the loading to true until the page change
            } else {
              this.loading = false;
              this.passwordField.nativeElement.focus();
            }
          },
        });
    } else {
      // discover user login method
      // always save the last user login
      this.authenticationService.saveUserLogin(userLogin);

      this.redirectLoginSub = this.authenticationService
        .redirectUserToLogin(userLogin, this.returnUrl, recaptchaToken)
        .subscribe({
          next: (idp) => {
            this.currentIdp = idp;
            if (IdentityProviderType.NONE === idp.type) {
              this.f('username').setErrors({ unknownEmail: true });
              this.loading = false;
            } else if (IdentityProviderType.B2C === idp.type) {
              this.changeB2cUser(true);
              this.loading = false;
            }
            // note : if the idp is a remote one, we let the loading to true while the Idp page is loading
          },
          error: () => (this.loading = false),
        });
    }
  }

  showDownloadApp() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(StoreBadgeDialogComponent, {});
    }
  }

  initSubscriptions() {
    this.credentialSub = this.bridgeInputService.credentials$.subscribe((credential: Credentials) => {
      this.f('username').setValue(credential.username || 'no');
      this.f('password').setValue(credential.password || 'no');
      this.onSubmit();
    });
  }

  resetLogin() {
    this.f('username').setValue('');
  }

  // subit if copy/paste password or auto-filled by native app
  onPasswordCHange(val: string) {
    if (this.passwordPreviousValue === '' && val.length > 1) {
      this.onSubmit();
    }
    this.passwordPreviousValue = val;
  }
}
